import { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, Facebook, Instagram, Linkedin } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useCMS } from "@/lib/CMSContext";
import { trackContactClicked } from "@/hooks/usePageTracking";

const Footer = () => {
  const { t } = useLanguage();
  const { cms } = useCMS();
  const navigate = useNavigate();
  const [clickCount, setClickCount] = useState(0);
  const [clickTimer, setClickTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const handleCopyrightClick = useCallback(() => {
    setClickCount((prev) => {
      const next = prev + 1;
      if (next >= 3) {
        navigate("/admin/login");
        return 0;
      }
      return next;
    });

    if (clickTimer) clearTimeout(clickTimer);
    setClickTimer(setTimeout(() => setClickCount(0), 1000));
  }, [clickTimer, navigate]);

  return (
    <footer className="bg-foreground text-primary-foreground py-16">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-4 gap-8 mb-12">
          <div className="md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              {cms.images.logo ? (
                <img src={cms.images.logo} alt="Vita Dom" className="h-8 w-auto" />
              ) : (
                <>
                  <Heart className="w-6 h-6 text-accent fill-accent/20" />
                  <span className="font-display text-xl">Vita Dom</span>
                </>
              )}
            </div>
            <p className="text-primary-foreground/60 text-sm leading-relaxed mb-6">
              {t("footer_desc")}
            </p>

            {/* Social media links */}
            <div className="flex items-center gap-3">
              <a
                href="#"
                aria-label="Facebook"
                className="w-9 h-9 rounded-full bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-colors"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href="#"
                aria-label="Instagram"
                className="w-9 h-9 rounded-full bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-colors"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="#"
                aria-label="LinkedIn"
                className="w-9 h-9 rounded-full bg-primary-foreground/10 flex items-center justify-center hover:bg-primary-foreground/20 transition-colors"
              >
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>
          <div>
            <h4 className="font-display text-lg mb-4">{t("footer_services")}</h4>
            <ul className="space-y-2 text-sm text-primary-foreground/60">
              <li>{t("service_errands")}</li>
              <li>{t("service_cooking")}</li>
              <li>{t("service_cleaning")}</li>
              <li>{t("service_walks")}</li>
              <li>{t("service_pharmacy")}</li>
              <li>{t("service_social")}</li>
            </ul>
          </div>
          <div>
            <h4 className="font-display text-lg mb-4">{t("footer_countries")}</h4>
            <ul className="space-y-2 text-sm text-primary-foreground/60">
              <li>🇷🇸 Serbia</li>
              <li>🇷🇴 Romania</li>
              <li>🇲🇰 North Macedonia</li>
              <li>🇧🇬 Bulgaria</li>
              <li>🇭🇺 Hungary</li>
            </ul>
          </div>
          <div>
            <h4 className="font-display text-lg mb-4">{t("footer_contact")}</h4>
            <ul className="space-y-2 text-sm text-primary-foreground/60">
              <li>
                <a
                  href={`mailto:${cms.contact.email}`}
                  onClick={() => trackContactClicked("footer")}
                  className="hover:text-primary-foreground transition-colors"
                >
                  {cms.contact.email}
                </a>
              </li>
              <li>{cms.contact.phone}</li>
              <li>{cms.contact.hours}</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-primary-foreground/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-primary-foreground/40">
          <span
            onClick={handleCopyrightClick}
            className="cursor-default select-none"
          >
            © {new Date().getFullYear()} {t("footer_rights")}
          </span>
          <div className="flex items-center gap-4">
            <Link
              to="/privacy"
              className="hover:text-primary-foreground/60 transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="hover:text-primary-foreground/60 transition-colors"
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
